import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'
import type { ProjectCard as ProjectCardData } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { motion, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { getReadableTone, resolveAccent } from '@/lib/color'
import { CountUp } from '@/components/motion/count-up'
import { ParallaxImage } from '@/components/motion/parallax-image'
import { EASE_OUT } from '@/constants/motion'

interface ProjectCardProps {
    project: ProjectCardData
    index: number
    total: number
    progress: MotionValue<number>
    stacking: boolean
}

const pad = (value: number): string => String(value).padStart(2, '0')

// One case study card. On wide screens the cards pin one after another and the
// ones underneath shrink back, like a deck being dealt as you scroll.
export const ProjectCard: FC<ProjectCardProps> = ({
    project,
    index,
    total,
    progress,
    stacking
}): ReactNode => {
    const accent = resolveAccent(project.accent, index)
    const tone = getReadableTone(accent)
    const targetScale = 1 - (total - index - 1) * 0.045
    const scale = useTransform(progress, [index / total, 1], stacking ? [1, targetScale] : [1, 1])
    const href = `/work/${encodeURIComponent(project.slug)}`
    const metric = project.results[0] ?? null

    return (
        <div className={cn('px-4 sm:px-8', stacking ? 'sticky top-0 flex h-[100svh] items-center' : 'py-3')}>
            <motion.article
                initial={{ opacity: 0, y: 70 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                transition={{ duration: 0.9, ease: EASE_OUT }}
                style={{
                    scale,
                    top: stacking ? `calc(${index * 28}px - 3vh)` : 0,
                    backgroundColor: accent
                }}
                className={cn(
                    'relative mx-auto grid w-full max-w-6xl origin-top overflow-hidden rounded-[2rem] shadow-[0_40px_90px_-45px_rgba(20,20,20,0.6)] md:h-[min(600px,calc(100svh_-_7rem))] md:grid-cols-2 md:rounded-[2.5rem]',
                    tone === 'light' ? 'text-white' : 'text-[#141414]'
                )}
            >
                <div className='flex flex-col justify-between gap-8 p-6 sm:p-8 md:p-12'>
                    <div>
                        <div className='flex flex-wrap items-center justify-between gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em]'>
                            <p>
                                {project.client && <span data-mdlfy-cms={`projects:${project.id}:client`}>{project.client}</span>}
                                {project.client && project.year && <span aria-hidden='true'> · </span>}
                                {project.year && <span data-mdlfy-cms={`projects:${project.id}:year`}>{project.year}</span>}
                            </p>

                            <p aria-hidden='true'>
                                {pad(index + 1)}/{pad(total)}
                            </p>
                        </div>

                        {project.category && (
                            <p className='mt-6 inline-flex rounded-full border border-current px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.14em]'>
                                <span data-mdlfy-cms={`projects:${project.id}:category`}>{project.category}</span>
                            </p>
                        )}

                        <h3 className='mt-4 font-heading text-[clamp(1.9rem,3.4vw,3.1rem)] font-extrabold leading-[1.02] tracking-[-0.025em]'>
                            <Link href={href} className='decoration-2 underline-offset-[6px] hover:underline'>
                                <span data-mdlfy-cms={`projects:${project.id}:title`}>{project.title}</span>
                            </Link>
                        </h3>

                        {project.summary && (
                            <p
                                data-mdlfy-cms={`projects:${project.id}:summary`}
                                className='mt-4 line-clamp-3 max-w-md text-base leading-relaxed md:text-lg'
                            >
                                {project.summary}
                            </p>
                        )}
                    </div>

                    <div className='flex flex-wrap items-end justify-between gap-6'>
                        {metric && (
                            <div>
                                <p className='font-heading text-5xl font-extrabold leading-none tracking-[-0.04em] md:text-6xl'>
                                    {metric.prefix && <span data-mdlfy-cms={`project_results:${metric.id}:prefix`}>{metric.prefix}</span>}
                                    <CountUp value={metric.value} />
                                    {metric.suffix && <span data-mdlfy-cms={`project_results:${metric.id}:suffix`}>{metric.suffix}</span>}
                                </p>

                                <p className='mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em]'>
                                    <span data-mdlfy-cms={`project_results:${metric.id}:label`}>{metric.label}</span>
                                </p>
                            </div>
                        )}

                        <Link
                            href={href}
                            className={cn(
                                'group inline-flex items-center gap-2 rounded-full px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.14em] transition-transform duration-300 hover:-translate-y-0.5',
                                tone === 'light' ? 'bg-white text-[#141414]' : 'bg-[#141414] text-white'
                            )}
                        >
                            Read the case study
                            <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                        </Link>
                    </div>
                </div>

                <Link
                    href={href}
                    tabIndex={-1}
                    aria-hidden='true'
                    className='relative order-first block min-h-[240px] overflow-hidden sm:min-h-[320px] md:order-last md:m-3 md:min-h-0 md:rounded-[2rem]'
                >
                    {project.cover ? (
                        <ParallaxImage
                            src={project.cover}
                            alt={project.cover_alt ?? ''}
                            className='absolute inset-0'
                            imageClassName='transition-[scale] duration-700 hover:scale-105'
                        />
                    ) : (
                        <span className='absolute inset-0 flex items-center justify-center bg-black/10 font-heading text-8xl font-extrabold'>
                            {pad(index + 1)}
                        </span>
                    )}
                </Link>
            </motion.article>
        </div>
    )
}
